/**
 * x-swatch-overflow directive.
 *
 * Alpine directive that manages color swatch overflow in product cards.
 * When a product has more than 7 color options, this directive dynamically
 * shows/hides swatches based on available container width and displays a
 * "+N" counter for the hidden ones.
 *
 * Lifecycle:
 *   1. CSS fallback hides swatches 8+ via :nth-child(n+8) before JS loads.
 *   2. IntersectionObserver defers initialisation until the card is near
 *      the viewport (200px margin).
 *   3. On init, adds .swatch-overflow-ready to disable the CSS fallback,
 *      then runs the first calculation.
 *   4. ResizeObserver (on parent) and MutationObserver recalculate on
 *      container resize or DOM changes (e.g. after $morph).
 *
 * The container element must have `morph-child-only` to prevent Alpine's
 * morph from stripping the .swatch-overflow-ready class.
 *
 * @markup
 *   Defined in ui-variant-option-values-color.liquid:
 *   <div x-swatch-overflow morph-child-only>
 *     <label class="variant-option-value">…</label>
 *     <span data-swatch-overflow-count hidden>+0</span>
 *   </div>
 *
 * @example
 *   <div x-swatch-overflow morph-child-only>
 *     <label class="variant-option-value">…</label>
 *     <span data-swatch-overflow-count hidden>+0</span>
 *   </div>
 */
(() => {
  document.addEventListener("alpine:init", () => {
    Alpine.directive("swatch-overflow", (el, {}, { cleanup }) => {
      const countEl = el.querySelector("[data-swatch-overflow-count]");

      if (!countEl) return;

      /** @type {string} CSS selector for individual swatch elements. */
      const SELECTOR = ".variant-option-value";
      /** @type {number} Maximum number of swatches before overflow logic activates. */
      const THRESHOLD = 7;

      /**
       * Returns all swatch elements currently in the container.
       * @returns {Element[]}
       */
      const getSwatches = () => Array.from(el.querySelectorAll(SELECTOR));

      /**
       * Determines how many swatches fit in the available container width.
       * First checks whether all swatches fit without the counter element.
       * Only measures the counter when overflow is actually needed, to avoid
       * the counter's own width falsely reducing capacity.
       * @param {Element[]} swatches - The current list of swatch elements.
       * @returns {number} The number of swatches that can be displayed.
       */
      const measureCapacity = (swatches) => {
        const containerWidth = el.parentElement?.clientWidth || el.clientWidth;
        const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
        const sample = swatches.find((s) => !s.hidden) || swatches[0];
        const swatchWidth = sample.offsetWidth;

        if (swatchWidth <= 0) return 1;

        const totalWidth =
          swatches.length * swatchWidth + (swatches.length - 1) * gap;

        if (totalWidth <= containerWidth) return swatches.length;

        countEl.hidden = false;
        countEl.textContent = `+${swatches.length}`;

        const available = containerWidth - countEl.offsetWidth - gap;
        countEl.hidden = true;

        const count = Math.floor((available + gap) / (swatchWidth + gap));

        return Math.max(1, Math.min(count, swatches.length));
      };

      /**
       * Builds a Set of swatch indices that should be visible. If the checked
       * swatch falls outside the visible range, it replaces the last visible
       * slot — flex layout places it at the row's end since hidden elements
       * take no space (no DOM reordering needed).
       * @param {Element[]} swatches - The current list of swatch elements.
       * @param {number} capacity - The number of swatches that fit.
       * @returns {Set<number>} The set of indices to show.
       */
      const resolveVisible = (swatches, capacity) => {
        const show = new Set();

        for (let i = 0; i < capacity; i++) show.add(i);

        const checked = swatches.findIndex((s) =>
          s.querySelector("input:checked"),
        );

        if (checked >= capacity) {
          show.delete(capacity - 1);
          show.add(checked);
        }

        return show;
      };

      /**
       * Toggles the `hidden` attribute on each swatch and updates the "+N" counter.
       * @param {Element[]} swatches - The current list of swatch elements.
       * @param {Set<number>} show - The set of indices to show.
       */
      const apply = (swatches, show) => {
        for (let i = 0; i < swatches.length; i++) {
          swatches[i].hidden = !show.has(i);
        }

        const hiddenCount = swatches.length - show.size;
        countEl.textContent = `+${hiddenCount}`;
        countEl.hidden = hiddenCount <= 0;
      };

      /** @type {MutationObserverInit} MutationObserver configuration. */
      const moConfig = {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["checked", "data-option-value-id"],
      };

      let mo, ro;

      /**
       * Reconnects the MutationObserver in the next animation frame to avoid
       * re-entrancy from mutations triggered during the calculation itself.
       * The ResizeObserver is left running continuously — reconnecting it would
       * cause it to fire an initial notification on every calculate(), creating
       * an infinite loop.
       */
      const reconnect = () => {
        requestAnimationFrame(() => {
          mo.observe(el, moConfig);
        });
      };

      /**
       * Core calculation pipeline: disconnects the MutationObserver, measures
       * capacity, resolves visible indices, applies visibility, then reconnects.
       * Skips overflow logic when the swatch count is at or below the threshold.
       */
      const calculate = () => {
        mo.disconnect();

        const swatches = getSwatches();

        if (swatches.length <= THRESHOLD) {
          for (const s of swatches) s.hidden = false;
          countEl.hidden = true;
          reconnect();
          return;
        }

        const capacity = measureCapacity(swatches);

        if (capacity >= swatches.length) {
          for (const s of swatches) s.hidden = false;
          countEl.hidden = true;
          reconnect();
          return;
        }

        apply(swatches, resolveVisible(swatches, capacity));
        reconnect();
      };

      // Defer all work until the card is near the viewport.
      const io = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          io.disconnect();

          mo = new MutationObserver(() => calculate());
          ro = new ResizeObserver(() => calculate());
          ro.observe(el.parentElement || el);

          el.classList.add("swatch-overflow-ready");
          calculate();
        },
        { rootMargin: "200px" },
      );

      io.observe(el);

      cleanup(() => {
        io.disconnect();
        ro?.disconnect();
        mo?.disconnect();
      });
    }).before("bind");
  });
})();
